import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  errorObjectBuilder,
  exactValidator,
} from '../shared/validators/registration-validators';
import { isDateInFuture } from './time';

/**
 * Sets a form control validator as same value as another field
 * @param {AbstractControl} firstField
 * @param {AbstractControl} secondField
 * @param {ValidatorFn[]} previousValidators validators to preserve
 */
export function sameValueTwoFields(
  firstField: AbstractControl,
  secondField: AbstractControl,
  previousValidators: ValidatorFn[]
) {
  firstField.valueChanges.subscribe((value) => {
    secondField.clearValidators();
    secondField.setValidators([...previousValidators, exactValidator(value)]);
    secondField.updateValueAndValidity();
  });
}

/**
 * Validator that blocks dates in the future
 * @return {ValidatorFn}
 */
export function noFutureDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const errorObject = errorObjectBuilder('invalid-future-date');
    const givenDate: Date = control.value;
    if (isDateInFuture(givenDate)) {
      return errorObject;
    }
    return null;
  };
}

/**
 * Returns first error from form controls inside form group
 * @return {ValidatorFn}
 */
export function firstNestedError(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const formArray = control.get('formData') as FormArray;
    if (!formArray) return null;

    for (const group of formArray.controls as FormGroup[]) {
      for (const controlName of Object.keys(group.controls)) {
        const formControl = group.get(controlName);
        if (formControl?.errors) {
          return { field: controlName, error: formControl.errors };
        }
      }
    }

    return null;
  };
}

/**
 * Gets a date that happened some years ago
 * @param {string} translationKey
 * @return {ValidatorFn}
 */
export function realDateValidator(
  translationKey: string = 'invalid-calendar-date'
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value == null) {
      return null;
    }

    if (value instanceof Date && isNaN(value.getTime())) {
      return { invalidDate: translationKey };
    }

    if (value && value._isAMomentObject && !value.isValid()) {
      return { invalidDate: translationKey };
    }

    return null;
  };
}
